import React, { useCallback, useEffect, useMemo } from "react";

import {
  InputCst,
  SelectCst,
  AutocompleteCst,
  RadioCst,
  DataPickerCst,
  MultiSelectCst,
} from "@components/InputCst";
import { FilterBoxInterface } from "./types";
import ButtonCst from "@components/ButtonCst";
import { Box, Grid } from "@mui/material";
import { useFilters } from "@context/filters";
import CheckboxCst from "@components/InputCst/CheckboxCst";
import { useTranslation } from "react-i18next";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import dayjs from "dayjs";
import { Refresh, Search } from "@mui/icons-material";
import { useAuth } from "@context/auth";
import AutocompleteEventLabelCst from "@components/InputCst/AutocompleteEventLabelCst";
import { slugify } from "@utils/utilsTestIds";

const FilterBox: React.FC<FilterBoxInterface> = (props) => {
  const { t } = useTranslation(["form"]);

  const { id, columns, filtersException, testIdPrefix } = props;

  const filterFieldPrefix = (accessorKey: string) =>
    testIdPrefix ? `${testIdPrefix}-filter-${accessorKey}` : undefined;

  const buildSelectOptionTestIds = (accessorKey: string) => {
    const prefix = filterFieldPrefix(accessorKey);
    if (!prefix) return undefined;
    return (option: any) => {
      const rawValue = (option?.value ?? option?.id ?? "").toString();
      const slug = slugify(rawValue.replace(/^DA_/i, ""));
      return {
        id: `${prefix}-option-${slug}`,
        testId: `${prefix}-select`,
      };
    };
  };

  const buildSelectOptionAria = (label?: string) => {
    if (!label) return undefined;
    return (option: any) =>
      t("testAutomation.selectOptionAria", {
        label,
        option: option?.label || option?.name || "",
      }) || undefined;
  };

  const buildFieldLabelAria = (label?: string) => {
    if (!testIdPrefix || !label) return undefined;
    return t("testAutomation.fieldLabelAria", { label }) || undefined;
  };

  const buildDatePickerAria = (label?: string) => {
    if (!testIdPrefix || !label) return undefined;
    return t("testAutomation.datePickerOpenAria", { label }) || undefined;
  };
  const { user } = useAuth();
  const { filters, setFilters } = useFilters();
  const filterId = id ? `${user?.id_user}_${id}` : "";
  const [filterForm, setFilterForm] = React.useState<any>(
    filters[filterId] || {},
  );

  useEffect(() => {
    setFilterForm(filters[filterId] || {});
  }, [filters, filterId]);

  const resetFilterForm = () => {
    const resetForm = { pageIndex: 0, pageSize: 20 };
    setFilterForm({});
    setFilters(filterId, resetForm);
  };

  const filteredColumns = useMemo(() => {
    if (filterForm?.type !== "DA_URL_RES") {
      delete filterForm?.is_link_relative;
      delete filterForm?.is_editorial;
      return columns?.filter(
        (col) =>
          col.accessorKey !== "is_link_relative" &&
          col.accessorKey !== "is_editorial",
      );
    }
    return columns;
  }, [filterForm, columns]);

  const getFilter = useMemo(() => {
    return filteredColumns.map((filter: any, index) => {
      if (filtersException) {
        filter = filtersException(filter, filterForm);
      }
      switch (filter.type) {
        case "autoIncrement": {
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <InputCst
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                maxValue={filter?.rules?.max?.value}
                multiline={filter.multiline}
                type={filter.inpuType || "text"}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e.target.value },
                  })
                }
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
                ariaLabelOverride={buildFieldLabelAria(filter.header)}
              />
            </Grid>
          );
        }
        case "description":
        case "association":
        case "text": {
          if (filter.accessorKey === "event.label") {
            return (
              <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
                <AutocompleteEventLabelCst
                  id={filter.accessorKey}
                  label={filter.header}
                  value={filterForm[filter.accessorKey] || null}
                  onChange={(e: any) =>
                    setFilterForm({
                      ...filterForm,
                      [filter.accessorKey]: e?.value || "",
                    })
                  }
                  options={filter.options || []}
                  isFilterActive={!!filterForm[filter.accessorKey]}
                />
              </Grid>
            );
          }
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <InputCst
                id={filter.accessorKey}
                label={filter.header}
                value={decodeURIComponent(filterForm[filter.accessorKey] || "")}
                maxValue={filter?.rules?.max?.value}
                multiline={filter.multiline}
                type={filter.inpuType || "text"}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{
                      [filter.accessorKey]: encodeURIComponent(e.target.value),
                    },
                  })
                }
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
                ariaLabelOverride={buildFieldLabelAria(filter.header)}
              />
            </Grid>
          );
        }
        case "select-checkbox":
        case "iconText":
        case "dynamicSelect":
        case "association":
        case "switch":
        case "select": {
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <SelectCst
                id={filter.accessorKey}
                label={filter.header}
                optionsKey={filter.optionsKey}
                value={filterForm[filter.accessorKey]}
                optionsToDisable={filter.optionsToDisable}
                service={filter.service}
                onChange={(e: any) => {
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e },
                  });
                }}
                options={filter.options || []}
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
                ariaLabelOverride={
                  filter.header
                    ? t("testAutomation.fieldLabelAria", {
                        label: filter.header,
                      }) || undefined
                    : undefined
                }
                optionTestId={buildSelectOptionTestIds(filter.accessorKey)}
                optionAriaLabel={buildSelectOptionAria(filter.header)}
              />
            </Grid>
          );
        }
        case "multiCheckbox": {
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <MultiSelectCst
                id={filter.accessorKey}
                label={filter.header}
                optionsKey={filter.optionsKey}
                value={filterForm[filter.accessorKey]}
                service={filter.service}
                onChange={(e: any) => {
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e },
                  });
                }}
                options={filter.options || []}
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
              />
            </Grid>
          );
        }
        case "autocomplete": {
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <AutocompleteCst
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e },
                  })
                }
                options={filter.options}
                isFilterActive={!!filterForm[filter.accessorKey]}
              />
            </Grid>
          );
        }
        case "radio": {
          return (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <RadioCst
                key={filter.accessorKey}
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e },
                  })
                }
                options={[
                  { value: "10", label: "Flower" },
                  { value: "20", label: "Engineer" },
                ]}
              />
            </Grid>
          );
        }
        case "checkbox": {
          return (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <CheckboxCst
                key={filter.accessorKey}
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{ [filter.accessorKey]: e },
                  })
                }
                options={[
                  { value: "10", label: "Flower" },
                  { value: "20", label: "Engineer" },
                ]}
              />
            </Grid>
          );
        }
        case "dateTimeYear": {
          return (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <DataPickerCst
                views={["year"]}
                key={filter.accessorKey}
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                onChange={(e) => {
                  const date = new Date(e);
                  const year = date.getFullYear();
                  setFilterForm({
                    ...filterForm,
                    ...{
                      [filter.accessorKey]: year.toString(),
                    },
                  });
                }}
                required={false}
                error={false}
                disabled={false}
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
                openPickerAriaLabel={buildDatePickerAria(filter.header)}
              />
            </Grid>
          );
        }
        case "dateTime": {
          return (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <DataPickerCst
                key={filter.accessorKey}
                id={filter.accessorKey}
                label={filter.header}
                value={filterForm[filter.accessorKey]}
                onChange={(e: any) => {
                  const date = new Date(e);
                  const dateUTC = Date.UTC(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate(),
                    0,
                    0,
                    0,
                  );
                  setFilterForm({
                    ...filterForm,
                    ...{
                      [filter.accessorKey]: new Date(dateUTC).toISOString(),
                    },
                  });
                }}
                required={false}
                error={false}
                disabled={false}
                isFilterActive={!!filterForm[filter.accessorKey]}
                testIdPrefix={filterFieldPrefix(filter.accessorKey)}
                openPickerAriaLabel={buildDatePickerAria(filter.header)}
              />
            </Grid>
          );
        }
        case "time": {
          return (
            <Grid key={filter.accessorKey} item xs={12} sm={6} md={4} lg={3}>
              <TimePickerCst
                id={filter.accessorKey}
                value={filterForm[filter.accessorKey]}
                label={filter.header}
                onChange={(e: any) =>
                  setFilterForm({
                    ...filterForm,
                    ...{
                      [filter.accessorKey]: dayjs(e).format(
                        "YYYY-MM-DDTHH:mm:ss.SSS",
                      ),
                    },
                  })
                }
              />
            </Grid>
          );
        }
      }
    });
  }, [filteredColumns, filterForm]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setFilters(filterId, {
          ...filterForm,
          ...{
            pageIndex: 0,
            pageSize: filters[filterId]?.pageSize
              ? filters[filterId]?.pageSize
              : 20,
          },
        });
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{ p: "16px 0 48px 24px", mt: 0, width: "100%" }}
      >
        {getFilter}
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ ml: "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "16px",
            }}
          >
            <ButtonCst
              id={
                testIdPrefix
                  ? `${testIdPrefix}-filters-reset-button`
                  : "filters-reset-submit-button"
              }
              data-testid={
                testIdPrefix
                  ? `${testIdPrefix}-filters-reset`
                  : "filters-reset-submit"
              }
              aria-label={t("testAutomation.filtersResetAria") || ""}
              variant={"outlined"}
              onClick={() => {
                // setFilters && setFilters(`${user?.id_user}_${id}`, {});
                resetFilterForm();
              }}
              size={"medium"}
              title={t("resetButton") || ""}
              sx={{
                minWidth: "40px",
                padding: "0 15px",
              }}
            >
              <Refresh />
            </ButtonCst>
            <ButtonCst
              id={
                testIdPrefix
                  ? `${testIdPrefix}-filters-search-button`
                  : "filters-search-submit-button"
              }
              data-testid={
                testIdPrefix
                  ? `${testIdPrefix}-filters-search`
                  : "filters-search-submit"
              }
              aria-label={t("testAutomation.filtersSearchAria") || ""}
              variant={"contained"}
              size={"medium"}
              type="submit"
              title={t("searchButton") || ""}
              sx={{
                minWidth: "40px",
                padding: "0 15px",
              }}
            >
              <Search />
            </ButtonCst>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterBox;
